/**
 * Pay Charge Embed Library
 *
 * A thin wrapper that simplifies payment integration by embedding
 * the payment form in an iframe with a simple API.
 *
 * @example Iframe mode (embedded form)
 * ```html
 * <div id="payment-form"></div>
 * <script src="pay-embed.js"></script>
 * <script>
 *   const embed = PayChargeEmbed.configure({
 *     containerId: 'payment-form',
 *     sessionToken: 'eyJ...'
 *   });
 *
 *   embed.addEventListener('charge:success', (e) => {
 *     console.log('Charge ID:', e.detail.chargeId);
 *   });
 *
 *   embed.initialize({ amount: 10.00 });
 * </script>
 * ```
 *
 * @example Redirect mode (full-page checkout)
 * ```html
 * <script src="pay-embed.js"></script>
 * <script>
 *   // Option 1: Using PayChargeEmbed
 *   const embed = PayChargeEmbed.configure({
 *     sessionToken: 'eyJ...'
 *   });
 *   embed.initialize({ mode: 'redirect' }); // Redirects to checkout page
 *
 *   // Option 2: Using helper function
 *   redirectToCheckout({ sessionToken: 'eyJ...' });
 * </script>
 * ```
 */

export { PayChargeEmbed } from './PayChargeEmbed';
export * from './types';

import { PayChargeEmbed } from './PayChargeEmbed';
import { RedirectToCheckoutOptions } from './types';

/**
 * Convenience function to redirect to the hosted checkout page.
 * Decodes the session ID from the JWT and redirects to /checkout/{sessionId}.
 *
 * @param options - Options including the session token and optional base URL
 * @throws Error if the session ID cannot be extracted from the token
 */
export function redirectToCheckout(options: RedirectToCheckoutOptions): void {
  const { sessionToken, baseUrl = window.location.origin } = options;

  // Decode session ID from JWT
  const sessionId = decodeSessionIdFromToken(sessionToken);
  if (!sessionId) {
    throw new Error('[PayChargeEmbed] Failed to extract session ID from token');
  }

  // Build checkout URL (session ID as query param)
  const checkoutUrl = `${baseUrl}/checkout/?session=${sessionId}`;
  // Not logged - see PayChargeEmbed.redirectToCheckout. NIT review of #1101.

  window.location.href = checkoutUrl;
}

/**
 * Decode session ID from JWT token (without verification).
 */
function decodeSessionIdFromToken(token: string): string | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[PayChargeEmbed] Invalid JWT format');
      return null;
    }

    // Decode base64url payload
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed = JSON.parse(decoded);

    // Session ID is stored in 'sid' claim
    return parsed.sid || null;
  } catch (error) {
    console.error('[PayChargeEmbed] Failed to decode JWT:', error);
    return null;
  }
}

// For UMD builds, expose PayChargeEmbed and redirectToCheckout globally
if (typeof window !== 'undefined') {
  (window as any).PayChargeEmbed = PayChargeEmbed;
  (window as any).redirectToCheckout = redirectToCheckout;
}
